<template>
  <div class="ml-2">
    <icon-view
      class="custom-icon-view mb-3"
      :has-border="false"
      icon="fas fa-info-circle color-orange"
      title="Certificación Medicaid"
    />
    <validation-observer ref="observer">
      <validation-provider v-slot="{ errors }" rules="required">
        <div class="data-name mb-2">
          ¿La escuela se encuentra certificada?
        </div>
        <custom-radio
          is-inline
          v-model="model.certified"
          name="certified"
          class="extended_radio"
          :is-disabled="readOnly"
          :options="yesNoOptions"
          :error="Boolean(errors[0])"
          custom-error-msg="Favor marque sí o no."
        />
      </validation-provider>

      <div v-if="model.certified" class="row mt-3">
        <div class="col-12 col-md">
          <validation-provider
            class="w-100"
            rules="required"
            v-slot="{ errors }"
          >
            <div class="data-name mb-2">
              Fecha de Certificación:
            </div>
            <custom-date-picker
              placeholder="DD/MM/YYYY"
              container-class="w-100"
              v-model="model.certificationDate"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
              :is-disabled="readOnly"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md">
          <validation-provider
            class="w-100"
            rules="required"
            v-slot="{ errors }"
          >
            <div class="data-name mb-2">
              Fecha de Vigencia:
            </div>
            <custom-date-picker
              placeholder="DD/MM/YYYY"
              container-class="w-100"
              v-model="model.expirationDate"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
              :is-disabled="readOnly"
            />
          </validation-provider>
        </div>
      </div>
      <div v-else-if="model.certified === false" class="mt-3">
        <validation-provider class="w-100" rules="required" v-slot="{ errors }">
          <div class="data-name mb-2">
            Comentario:
          </div>
          <custom-text-area
            v-model="model.comment"
            :maxlength="3000"
            :error="errors[0]"
            :custom-error-msg="errors[0]"
            show-char-count
            :count-classes="['d-block', 'small-counter', 'text-right']"
            :disabled="readOnly"
            placeholder="Comentario de justificación de escuela no certificada."
          />
        </validation-provider>
      </div>
      <div class="row" :class="{ 'mt-3': model.certified !== false }">
        <div class="col-12 col-md">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.documentType"
              :class="{
                'was-Invalidated-select': !!errors[0],
              }"
              custom-error-msg="El campo es requerido."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              label="Tipo de Documento:"
              :options="documentTypes"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div v-if="isOtherDocument" class="col-12 col-md">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="model.other"
              custom-error-msg="El campo es requerido."
              :error="errors[0]"
              label="Nombre del Documento:"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-6">
          <div class="data-name mb-2">
            Archivo:
          </div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-file-input
              ref="fileUpload"
              v-model="model.file"
              :accept="allowedFiles"
              custom-error-msg="El documento es requerido"
              display-style="normal"
              :error="errors[0]"
              :on-download="() => {}"
            />
          </validation-provider>
        </div>
      </div>

      <div class="d-flex justify-content-end align-items-center mt-4">
        <action-button
          i="fas fa-times-circle"
          text="Cancelar"
          variant="outline-danger mr-2"
          @click="$emit('cancel')"
        />
        <action-button
          i="fas fa-save"
          text="Guardar"
          variant="success"
          @click="save"
        />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import IconView from 'icon-view';
import CustomInput from 'CustomInput';
import CustomRadio from 'custom-radio';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import ActionButton from 'ActionButton';
import CustomDatePicker from 'custom-date-picker';
import CustomTextArea from 'CustomTextArea';

import DocumentTypes from '@/utils/constants/documentTypes';

export default {
  name: 'MedicaidCertificationForm',
  components: {
    IconView,
    CustomInput,
    CustomRadio,
    CustomDropDown,
    CustomFileInput,
    CustomDatePicker,
    CustomTextArea,
    ActionButton,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    documentTypes: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {};
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    yesNoOptions() {
      return [
        {
          value: true,
          label: 'Sí',
          check: this.model.certified === true,
        },
        {
          value: false,
          label: 'No',
          check: this.model.certified === false,
        },
      ];
    },
    allowedFiles() {
      return ['.pdf'].join(',');
    },
    isOtherDocument() {
      return (
        this.model.documentType?.id ===
        DocumentTypes.OtherMedicaidCertificationDocument
      );
    },
  },
  async mounted() {
    this.clear();
  },
  methods: {
    async save() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;
      this.$emit('save', { ...this.model });
    },
    clear() {
      this.model = {};
      this.$refs.observer.reset();
      this.$refs.fileUpload.removeFile();
    },
  },
};
</script>

<style scoped>
.custom-icon-view ::v-deep > div:first-child {
  background-color: white !important;
  margin-right: 10px !important;
}

.custom-icon-view ::v-deep > div:last-child {
  font-weight: normal !important;
  color: black !important;
}

.extended_radio ::v-deep .col-12 {
  padding-left: 0 !important;
}
::v-deep .custom-datetime-picker-container.width-container {
  width: 100% !important;
}
</style>
